import { useState } from 'react'
import type { Patient } from '@medplum/fhirtypes'
import { formatHumanName } from '@medplum/core'
import { notifications } from '@mantine/notifications'
import { generateAuthorizationFormPdf } from '../OrderTests/pdf/generateAuthorizationFormPdf'
import { downloadPdfBlob, pdfBytesToBase64 } from '../OrderTests/pdf/pdfHelpers'
import { nameToDataUrl } from '../OrderTests/nameToSignatureDataUrl'
import type { AuthorizationFormState } from './authorizationFormData'
import { useMiddlewareClient } from '@/hooks/useMiddlewareClient'
import { logError } from '@/utils/errorUtils'

export function useAuthorizationPdf(
  patient: Patient,
  form: AuthorizationFormState,
  signed: boolean,
  signedDate?: string,
) {
  const middleware = useMiddlewareClient()
  const [previewing, setPreviewing] = useState(false)
  const [sending, setSending] = useState(false)

  const pdfFileName = () =>
    `Authorization_${patient.name?.[0]?.family ?? 'Patient'}_${new Date().toISOString().slice(0, 10)}.pdf`

  // Build the filled authorization PDF — identical bytes for preview and fax.
  const buildPdfBytes = async (): Promise<Uint8Array> => {
    const patientName = patient.name?.[0] ? formatHumanName(patient.name[0]) : ''
    const signatureDataUrl = signed && patientName ? await nameToDataUrl(patientName) : ''
    const signatureDate = signed
      ? signedDate
        ? new Date(signedDate).toLocaleDateString('en-US')
        : new Date().toLocaleDateString('en-US')
      : ''
    return generateAuthorizationFormPdf(patient, form, signatureDataUrl, signatureDate)
  }

  const handlePreviewPdf = async () => {
    setPreviewing(true)
    try {
      const bytes = await buildPdfBytes()
      const blob = new Blob([bytes], { type: 'application/pdf' })
      downloadPdfBlob(blob, pdfFileName())
      notifications.show({
        title: 'PDF Preview',
        message: 'The authorization form has been downloaded for preview.',
        color: 'blue',
      })
    } catch (err) {
      logError('AuthorizationRelease', 'preview authorization PDF', err)
      notifications.show({
        title: 'PDF Export Failed',
        message: err instanceof Error ? err.message : 'Unknown error',
        color: 'red',
      })
    } finally {
      setPreviewing(false)
    }
  }

  // Fax the filled authorization to the facility — mirrors the requisition fax flow.
  const handleSendFax = async (): Promise<boolean> => {
    const faxNumber = form.facility.fax?.trim()
    if (!faxNumber) {
      notifications.show({
        title: 'No Fax Number',
        message: 'The selected facility has no fax number on file.',
        color: 'red',
      })
      return false
    }
    setSending(true)
    try {
      const pdfBase64 = pdfBytesToBase64(await buildPdfBytes())
      await middleware.post('/order/send-fax', {
        faxNumber,
        pdfBase64,
        pdfFileName: pdfFileName(),
      })
      notifications.show({
        title: 'Fax Sent',
        message: `The authorization was faxed to ${form.facility.name || 'the facility'} (${faxNumber}).`,
        color: 'green',
      })
      return true
    } catch (err) {
      logError('AuthorizationRelease', 'send authorization fax', err)
      notifications.show({
        title: 'Fax Sending Failed',
        message: err instanceof Error ? err.message : 'Unknown error',
        color: 'red',
      })
      return false
    } finally {
      setSending(false)
    }
  }

  return { handlePreviewPdf, previewing, handleSendFax, sending }
}
